import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
    Radio,
    Search,
    RefreshCw,
    MapPin,
    Smartphone,
    Monitor,
    Clock,
    BellOff,
    Users,
    AlertTriangle
} from 'lucide-react';

const LivePresencePage = () => {
    const { api } = useAuth();
    const [loading, setLoading] = useState(true);
    const [presence, setPresence] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [refreshing, setRefreshing] = useState(false);
    const [lastRefresh, setLastRefresh] = useState(null);

    useEffect(() => {
        fetchPresence();
        
        // Auto-refresh every 10 seconds
        const interval = setInterval(fetchPresence, 10000);
        return () => clearInterval(interval);
    }, []);

    const fetchPresence = async () => {
        try {
            const res = await api().get('/messaging/presence/live');
            setPresence(res.data);
            setLastRefresh(new Date());
            setLoading(false);
        } catch (error) {
            toast.error('Erreur lors du chargement');
            setLoading(false);
        }
    };

    const handleRefresh = async () => {
        setRefreshing(true);
        await fetchPresence();
        setRefreshing(false);
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'ONLINE':
                return <div className="w-3 h-3 rounded-full bg-green-500 animate-pulse" />;
            case 'RECENTLY_ACTIVE':
                return <div className="w-3 h-3 rounded-full bg-yellow-500" />;
            case 'NOTIFICATIONS_DISABLED':
                return <BellOff className="w-3 h-3 text-gray-500" />;
            default:
                return <div className="w-3 h-3 rounded-full bg-red-500" />;
        }
    };

    const getStatusLabel = (status) => {
        switch (status) {
            case 'ONLINE':
                return 'En ligne';
            case 'RECENTLY_ACTIVE':
                return 'Actif récemment';
            case 'NOTIFICATIONS_DISABLED':
                return 'Notifs désactivées';
            default:
                return 'Hors ligne';
        }
    };

    const getStatusBadgeColor = (status) => {
        switch (status) {
            case 'ONLINE':
                return 'bg-green-500';
            case 'RECENTLY_ACTIVE':
                return 'bg-yellow-500';
            case 'NOTIFICATIONS_DISABLED':
                return 'bg-gray-500';
            default:
                return 'bg-red-500';
        }
    };

    const formatLastActivity = (dateStr) => {
        if (!dateStr) return 'Jamais';
        
        const date = new Date(dateStr);
        const now = new Date();
        const diff = Math.floor((now - date) / 1000);
        
        if (diff < 60) return 'À l\'instant';
        if (diff < 300) return `Il y a ${Math.floor(diff / 60)} min`;
        if (diff < 3600) return `Il y a ${Math.floor(diff / 60)} min`;
        if (diff < 86400) return `Il y a ${Math.floor(diff / 3600)} h`;
        return date.toLocaleDateString('fr-FR');
    };

    const filteredPresence = presence.filter(p =>
        p.user_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (p.service && p.service.toLowerCase().includes(searchQuery.toLowerCase())) ||
        p.role.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const stats = {
        online: presence.filter(p => p.status === 'ONLINE').length,
        recent: presence.filter(p => p.status === 'RECENTLY_ACTIVE').length,
        offline: presence.filter(p => p.status === 'OFFLINE').length,
        notifOff: presence.filter(p => p.status === 'NOTIFICATIONS_DISABLED').length
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-[calc(100vh-200px)]">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="live-presence-page">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="font-display text-2xl font-bold text-foreground tracking-tight flex items-center gap-2">
                        <Radio className="w-6 h-6 text-green-500" />
                        Présence Live
                    </h1>
                    <p className="text-sm text-muted-foreground">
                        Dernière mise à jour: {lastRefresh ? lastRefresh.toLocaleTimeString('fr-FR') : '-'}
                    </p>
                </div>
                <Button
                    variant="outline"
                    onClick={handleRefresh}
                    disabled={refreshing}
                >
                    <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                    Actualiser
                </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="bg-green-50 border-green-200">
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="w-4 h-4 rounded-full bg-green-500 animate-pulse" />
                            <div>
                                <p className="text-2xl font-bold text-green-700">{stats.online}</p>
                                <p className="text-xs text-green-600">En ligne</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                
                <Card className="bg-yellow-50 border-yellow-200">
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="w-4 h-4 rounded-full bg-yellow-500" />
                            <div>
                                <p className="text-2xl font-bold text-yellow-700">{stats.recent}</p>
                                <p className="text-xs text-yellow-600">Actifs récemment</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                
                <Card className="bg-red-50 border-red-200">
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="w-4 h-4 rounded-full bg-red-500" />
                            <div>
                                <p className="text-2xl font-bold text-red-700">{stats.offline}</p>
                                <p className="text-xs text-red-600">Hors ligne</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                
                <Card className="bg-gray-50 border-gray-200">
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <BellOff className="w-4 h-4 text-gray-500" />
                            <div>
                                <p className="text-2xl font-bold text-gray-700">{stats.notifOff}</p>
                                <p className="text-xs text-gray-600">Notifs off</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Search */}
            <Card>
                <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                        <CardTitle className="text-base flex items-center gap-2">
                            <Users className="w-5 h-5" />
                            Utilisateurs ({filteredPresence.length})
                        </CardTitle>
                        <div className="relative w-64">
                            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <Input
                                placeholder="Rechercher..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-8 h-9"
                            />
                        </div>
                    </div>
                </CardHeader>
                <CardContent>
                    <ScrollArea className="h-[500px]">
                        <div className="space-y-2">
                            {filteredPresence.map((user) => (
                                <div
                                    key={user.user_id}
                                    className={`p-4 rounded-lg border ${
                                        user.status === 'ONLINE' 
                                            ? 'bg-green-50/50 border-green-200' 
                                            : user.status === 'RECENTLY_ACTIVE'
                                                ? 'bg-yellow-50/50 border-yellow-200'
                                                : 'bg-muted/30 border-border'
                                    }`}
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-4">
                                            {/* Status indicator */}
                                            {getStatusIcon(user.status)}
                                            
                                            {/* User info */}
                                            <div>
                                                <div className="flex items-center gap-2">
                                                    <span className="font-medium">{user.user_name}</span>
                                                    <Badge variant="outline" className="text-xs">
                                                        {user.role}
                                                    </Badge>
                                                </div>
                                                <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                                                    {user.service && (
                                                        <span>{user.service}</span>
                                                    )}
                                                    {user.position && (
                                                        <span>• {user.position}</span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        
                                        <div className="flex items-center gap-4 text-sm">
                                            {/* Device */}
                                            <div className="flex items-center gap-1 text-muted-foreground">
                                                {user.device_type === 'mobile' ? (
                                                    <Smartphone className="w-4 h-4" />
                                                ) : (
                                                    <Monitor className="w-4 h-4" />
                                                )}
                                                <span className="text-xs">{user.device_type}</span>
                                            </div>
                                            
                                            {/* Location if available */}
                                            {user.latitude && user.longitude && (
                                                <div className="flex items-center gap-1 text-blue-500">
                                                    <MapPin className="w-4 h-4" />
                                                    <span className="text-xs">GPS</span>
                                                </div>
                                            )}
                                            
                                            {/* Last activity */}
                                            <div className="flex items-center gap-1 text-muted-foreground min-w-24">
                                                <Clock className="w-4 h-4" />
                                                <span className="text-xs">{formatLastActivity(user.last_activity)}</span>
                                            </div>
                                            
                                            {/* Status badge */}
                                            <Badge className={`text-white text-xs ${getStatusBadgeColor(user.status)}`}>
                                                {getStatusLabel(user.status)}
                                            </Badge>
                                        </div>
                                    </div>
                                </div>
                            ))}
                            
                            {filteredPresence.length === 0 && (
                                <div className="text-center py-12 text-muted-foreground">
                                    <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                                    <p>Aucun utilisateur trouvé</p>
                                </div>
                            )}
                        </div>
                    </ScrollArea>
                </CardContent>
            </Card>
        </div>
    );
};

export default LivePresencePage;
